import { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { MobileSearchAssistant } from '@mobileTypes'

const STORAGE_KEY = 'mobileSearchAssistants'

const getSearchAssistantsFromStorage = (): MobileSearchAssistant[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEY)

    return data ? JSON.parse(data) : []
  } catch (error) {
    console.error('Error reading mobile search assistants from localStorage:', error)

    return []
  }
}

const saveSearchAssistantsToStorage = (searchAssistants: MobileSearchAssistant[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(searchAssistants))
  } catch (error) {
    console.error('Error saving mobile search assistants to localStorage:', error)
  }
}

export const useMobileSearchAssistants = () => {
  const [searchAssistants, setSearchAssistants] = useState<MobileSearchAssistant[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadSearchAssistants = () => {
      setLoading(true)
      const loadedSearchAssistants = getSearchAssistantsFromStorage()
      setSearchAssistants(loadedSearchAssistants)
      setLoading(false)
    }

    loadSearchAssistants()
  }, [])

  const createSearchAssistant = useCallback(
    (
      searchAssistantData: Omit<MobileSearchAssistant, 'id' | 'createdAt' | 'updatedAt'>
    ): MobileSearchAssistant => {
      const newSearchAssistant: MobileSearchAssistant = {
        ...searchAssistantData,
        id: uuidv4(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }

      const allSearchAssistants = getSearchAssistantsFromStorage()
      const updatedSearchAssistants = [...allSearchAssistants, newSearchAssistant]
      setSearchAssistants(updatedSearchAssistants)
      saveSearchAssistantsToStorage(updatedSearchAssistants)

      return newSearchAssistant
    },
    []
  )

  const updateSearchAssistant = useCallback(
    (id: string, searchAssistantData: Partial<MobileSearchAssistant>) => {
      const allSearchAssistants = getSearchAssistantsFromStorage()
      const updatedSearchAssistants = allSearchAssistants.map(searchAssistant =>
        searchAssistant.id === id
          ? {
              ...searchAssistant,
              ...searchAssistantData,
              id: searchAssistant.id,
              createdAt: searchAssistant.createdAt,
              updatedAt: Date.now(),
            }
          : searchAssistant
      )

      setSearchAssistants(updatedSearchAssistants)
      saveSearchAssistantsToStorage(updatedSearchAssistants)
    },
    []
  )

  const deleteSearchAssistant = useCallback((id: string) => {
    const allSearchAssistants = getSearchAssistantsFromStorage()
    const updatedSearchAssistants = allSearchAssistants.filter(
      searchAssistant => searchAssistant.id !== id
    )
    setSearchAssistants(updatedSearchAssistants)
    saveSearchAssistantsToStorage(updatedSearchAssistants)
  }, [])

  const getSearchAssistantById = useCallback(
    (id: string): MobileSearchAssistant | undefined => {
      const allSearchAssistants = getSearchAssistantsFromStorage()

      return allSearchAssistants.find(searchAssistant => searchAssistant.id === id)
    },
    []
  )

  const copySearchAssistant = useCallback(
    (id: string, newName: string): MobileSearchAssistant | null => {
      const allSearchAssistants = getSearchAssistantsFromStorage()
      const searchAssistantToCopy = allSearchAssistants.find(
        searchAssistant => searchAssistant.id === id
      )
      if (!searchAssistantToCopy) return null

      const newSearchAssistant: MobileSearchAssistant = {
        ...searchAssistantToCopy,
        id: uuidv4(),
        name: newName,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }

      const updatedSearchAssistants = [...allSearchAssistants, newSearchAssistant]
      setSearchAssistants(updatedSearchAssistants)
      saveSearchAssistantsToStorage(updatedSearchAssistants)

      return newSearchAssistant
    },
    []
  )

  return {
    searchAssistants,
    loading,
    createSearchAssistant,
    updateSearchAssistant,
    deleteSearchAssistant,
    getSearchAssistantById,
    copySearchAssistant,
  }
}





